import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import PublicIcon from "@mui/icons-material/Public";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const PreviewContainer = styled("div")(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: "8px",
  border: "1px solid #E4E6EB",
  overflow: "hidden",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  maxWidth: "500px",
}));

const PostHeader = styled("div")(({ theme }) => ({
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const Avatar = styled("div")(({ theme }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  background: "#1877F2",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "hidden",

  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },

  "& svg": {
    width: "100%",
    height: "100%",
    fill: "#FFFFFF",
  },
}));

const UserInfo = styled("div")(({ theme }) => ({
  flex: 1,

  "& .user_name": {
    fontSize: "15px",
    fontWeight: "600",
    color: "#050505",
    lineHeight: "20px",
  },

  "& .post_time": {
    fontSize: "13px",
    color: "#65676B",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    lineHeight: "16px",
    marginTop: "2px",
  },
}));

const PostContent = styled("div")(({ theme }) => ({
  padding: "4px 16px 12px 16px",

  "& .caption": {
    fontSize: "15px",
    lineHeight: "20px",
    color: "#050505",
    whiteSpace: "pre-wrap",
    wordWrap: "break-word",
  },
}));

const CarouselContainer = styled("div")(({ theme }) => ({
  width: "100%",
  position: "relative",
  backgroundColor: "#000000",
  overflow: "hidden",
}));

const CarouselTrack = styled("div")(({ theme }) => ({
  display: "flex",
  transition: "transform 0.3s ease-in-out",
}));

const CarouselSlide = styled("div")(({ theme }) => ({
  minWidth: "100%",
  position: "relative",

  "& img, & video": {
    width: "100%",
    height: "auto",
    display: "block",
    maxHeight: "500px",
    objectFit: "contain",
  },
}));

const MediaPlaceholder = styled("div")(({ theme }) => ({
  width: "100%",
  height: "300px",
  background: "#F0F0F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#737373",
  fontSize: "14px",
  flexDirection: "column",
  gap: "8px",
}));

const VideoOverlay = styled("div")(({ theme, isPlaying }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60px",
  height: "60px",
  borderRadius: "50%",
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: isPlaying ? "none" : "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "opacity 0.2s",

  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "32px",
    color: "#FFFFFF",
  },
}));

const CarouselButton = styled("button")(({ theme, direction }) => ({
  position: "absolute",
  top: "50%",
  [direction]: "16px",
  transform: "translateY(-50%)",
  width: "36px",
  height: "36px",
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  zIndex: 2,

  "&:hover": {
    backgroundColor: "#FFFFFF",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    color: "#050505",
  },
}));

const CarouselDots = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "12px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: "6px",
  zIndex: 2,
}));

const Dot = styled("div")(({ theme, active }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: active ? "#FFFFFF" : "rgba(255, 255, 255, 0.5)",
  transition: "background-color 0.2s",
}));

const PostStats = styled("div")(({ theme }) => ({
  padding: "12px 16px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "15px",
  color: "#65676B",

  "& .reactions": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",

    "&:hover": {
      textDecoration: "underline",
    },
  },

  "& .engagement": {
    display: "flex",
    gap: "4px",
    cursor: "pointer",

    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const ReactionIconGroup = styled("div")(({ theme }) => ({
  display: "flex",
  marginRight: "4px",
}));

const ReactionIcon = styled("div")(({ theme, offset }) => ({
  width: "18px",
  height: "18px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  border: "1px solid #FFFFFF",
  marginLeft: offset ? "-4px" : "0",
}));

const PostActions = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "4px 8px",
  borderTop: "1px solid #E4E6EB",
}));

const ActionButton = styled("button")(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  padding: "6px 12px",
  background: "transparent",
  border: "none",
  borderRadius: "4px",
  fontSize: "15px",
  fontWeight: "600",
  color: "#65676B",
  transition: "background-color 0.2s",

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },
}));

// Facebook Logo SVG Component
const FacebookLogo = () => (
  <svg viewBox="0 0 36 36">
    <path
      d="M20.181 35.87C29.094 34.791 36 27.202 36 18c0-9.941-8.059-18-18-18S0 8.059 0 18c0 8.442 5.811 15.526 13.652 17.471L14 34h5.5l.681 1.87Z"
      fill="#1877F2"
    />
    <path
      d="M13.651 35.471v-11.97H9.936V18h3.715v-2.37c0-6.127 2.772-8.964 8.784-8.964 1.138 0 3.103.223 3.91.446v4.983c-.425-.043-1.167-.065-2.081-.065-2.952 0-4.09 1.116-4.09 4.025V18h5.883l-1.008 5.5h-4.867v12.37a18.183 18.183 0 0 1-6.53-.399Z"
      fill="#fff"
    />
  </svg>
);

const FacebookPreview = ({ modalData, showstats = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const userName = modalData?.userName || "User Name";
  const postTime = modalData?.postTime || "Posted At";
  const caption = modalData?.caption || "";
  const images = modalData?.images || [];
  const videoUrl = modalData?.videoUrl;
  const reactions = modalData?.reactions || 177;
  const comments = modalData?.comments || 42;
  const shares = modalData?.shares || 5;
  const profileImage = modalData?.profileImage;

  // Handle multiple images or single image
  const mediaItems =
    images.length > 0
      ? images
      : modalData?.imageUrl
        ? [modalData.imageUrl]
        : modalData?.videoUrl
          ? [modalData.videoUrl]
          : [];
  const hasMultipleImages = mediaItems.length > 1;

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % mediaItems.length);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + mediaItems.length) % mediaItems.length,
    );
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
  };

  const isVideoFile = (value) => {
    if (!value) return false;
    return /\.(mp4|mov|avi|webm|mkv)(\?|#|$)/i.test(value);
  };
  const isVideo =
    isVideoFile(videoUrl) || isVideoFile(mediaItems?.[currentSlide]);

  return (
    <PreviewContainer>
      <PostHeader>
        <Avatar>
          {profileImage ? (
            <img src={profileImage} alt={userName} />
          ) : (
            <FacebookLogo />
          )}
        </Avatar>
        <UserInfo>
          <div className="user_name">{userName}</div>
          <div className="post_time">
            {postTime} · <PublicIcon sx={{ fontSize: "12px" }} />
          </div>
        </UserInfo>
      </PostHeader>

      {caption && (
        <PostContent>
          <div className="caption">{caption}</div>
        </PostContent>
      )}

      <CarouselContainer>
        {mediaItems.length > 0 ? (
          <>
            <CarouselTrack
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {mediaItems.map((media, index) => (
                <CarouselSlide key={index}>
                  {isVideo && index === currentSlide ? (
                    <>
                      <video
                        ref={videoRef}
                        onClick={handleVideoClick}
                        onEnded={handleVideoEnded}
                        style={{ cursor: "pointer" }}
                      >
                        <source src={media} type="video/mp4" />
                      </video>
                      <VideoOverlay
                        onClick={handleVideoClick}
                        isPlaying={isPlaying}
                      >
                        <PlayArrowIcon />
                      </VideoOverlay>
                    </>
                  ) : (
                    <img src={media} alt={`Post content ${index + 1}`} />
                  )}
                </CarouselSlide>
              ))}
            </CarouselTrack>

            {hasMultipleImages && (
              <>
                {currentSlide > 0 && (
                  <CarouselButton direction="left" onClick={prevSlide}>
                    <ChevronLeftIcon />
                  </CarouselButton>
                )}
                {currentSlide < mediaItems.length - 1 && (
                  <CarouselButton direction="right" onClick={nextSlide}>
                    <ChevronRightIcon />
                  </CarouselButton>
                )}
                <CarouselDots>
                  {mediaItems.map((_, index) => (
                    <Dot key={index} active={index === currentSlide} />
                  ))}
                </CarouselDots>
              </>
            )}
          </>
        ) : (
          <MediaPlaceholder>
            <span>No Preview Available</span>
          </MediaPlaceholder>
        )}
      </CarouselContainer>

      {showstats && (
        <PostStats>
          <div className="reactions">
            <ReactionIconGroup>
              <ReactionIcon style={{ backgroundColor: "#1877F2" }}>
                👍
              </ReactionIcon>
              <ReactionIcon style={{ backgroundColor: "#F33E58" }} offset>
                ❤️
              </ReactionIcon>
              <ReactionIcon style={{ backgroundColor: "#F7B125" }} offset>
                😮
              </ReactionIcon>
            </ReactionIconGroup>
            <span>{reactions}</span>
          </div>
          <div className="engagement">
            <span>
              {comments} Comments · {shares} Shares
            </span>
          </div>
        </PostStats>
      )}

      <PostActions>
        <ActionButton>
          <ThumbUpOutlinedIcon />
          Like
        </ActionButton>
        <ActionButton>
          <ChatBubbleOutlineIcon />
          Comment
        </ActionButton>
        <ActionButton>
          <ReplyOutlinedIcon />
          Share
        </ActionButton>
      </PostActions>
    </PreviewContainer>
  );
};

export default FacebookPreview;
